import { useEffect, useState, useCallback } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { api, fmtMoney, fmtNumber, fmtDate } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ReportePersonalizado from "@/components/ReportePersonalizado";
import { useSort, SortTh } from "@/components/sortable";
import { CheckCircle, XCircle, Printer, CircleNotch, ArrowDown, ArrowUp, DownloadSimple } from "@phosphor-icons/react";

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

function Metric({ label, value, sub }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-md p-5">
      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#64748B]">{label}</p>
      <p className="text-2xl font-black tracking-tight text-[#0F172A] font-heading mt-1">{value}</p>
      {sub && <p className="text-xs text-[#64748B] mt-0.5">{sub}</p>}
    </div>
  );
}

export default function ReporteMensual() {
  const now = new Date();
  const [mes, setMes] = useState(now.getMonth() + 1);
  const [anio, setAnio] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const productosSort = useSort(data?.productos_detalle || []);
  const lotesSort = useSort(data?.lotes_detalle || []);
  const porDiaSort = useSort(data?.por_dia || []);
  const movimientosSort = useSort(data?.movimientos || []);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get(`/reporte-mensual?mes=${mes}&anio=${anio}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [mes, anio]);

  useEffect(() => {
    load();
  }, [load]);

  const exportPDF = () => {
    if (!data) return;
    const doc = new jsPDF();
    const mesNombre = MESES[mes - 1];
    doc.setFontSize(18);
    doc.text("Reporte mensual de inventario", 14, 18);
    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(`${mesNombre} ${anio} · Control de Inventario · NovusPak`, 14, 25);

    autoTable(doc, {
      startY: 32,
      theme: "plain",
      body: [
        ["Total ingresos", fmtNumber(data.total_ingresos) + " u"],
        ["Total salidas", fmtNumber(data.total_salidas) + " u"],
        ["Costo de ingresos", fmtMoney(data.costo_total_ingresos)],
        ["Valor inventario final", fmtMoney(data.valor_inventario_final)],
        ["Cuadratura", data.cuadra ? "OK (cuadra)" : "Inconsistente"],
      ],
      styles: { fontSize: 10 },
      columnStyles: { 0: { fontStyle: "bold", textColor: [15, 23, 42] } },
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      head: [["Producto", "Lotes", "Ingresos", "Salidas", "Stock final", "Costo ingresos", "Valor final"]],
      body: (data.productos_detalle || []).map((p) => [
        `${p.nombre} (${p.codigo})`,
        fmtNumber(p.num_lotes),
        "+" + fmtNumber(p.ingresos),
        "-" + fmtNumber(p.salidas),
        fmtNumber(p.stock_final),
        fmtMoney(p.costo_ingresos),
        fmtMoney(p.valor_final),
      ]),
      headStyles: { fillColor: [15, 23, 42] },
      styles: { fontSize: 9 },
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      head: [["Lote", "Stock inicial", "Ingresos", "Salidas", "Stock final"]],
      body: data.lotes_detalle.map((l) => [
        `${l.nombre} (${l.numero_lote})`,
        fmtNumber(l.stock_inicial),
        "+" + fmtNumber(l.ingresos),
        "-" + fmtNumber(l.salidas),
        fmtNumber(l.stock_final),
      ]),
      headStyles: { fillColor: [15, 23, 42] },
      styles: { fontSize: 9 },
    });

    doc.save(`reporte_${anio}_${String(mes).padStart(2, "0")}.pdf`);
  };

  const exportCSV = () => {
    if (!data) return;
    const mesNombre = MESES[mes - 1];
    const cell = (v) => {
      if (typeof v === "number") return String(v).replace(".", ",");
      const s = String(v ?? "");
      return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = [];
    rows.push([`Reporte mensual - ${mesNombre} ${anio}`]);
    rows.push([]);
    rows.push(["Resumen"]);
    rows.push(["Total ingresos (u)", data.total_ingresos]);
    rows.push(["Total salidas (u)", data.total_salidas]);
    rows.push(["Costo de ingresos", data.costo_total_ingresos]);
    rows.push(["Valor inventario final", data.valor_inventario_final]);
    rows.push(["Cuadratura", data.cuadra ? "OK" : "Inconsistente"]);
    rows.push([]);
    rows.push(["Costo total por producto"]);
    rows.push(["Producto", "Codigo", "Lotes", "Ingresos", "Salidas", "Stock final", "Costo ingresos", "Valor final"]);
    (data.productos_detalle || []).forEach((p) => {
      rows.push([p.nombre, p.codigo, p.num_lotes, p.ingresos, p.salidas, p.stock_final, p.costo_ingresos, p.valor_final]);
    });
    rows.push([]);
    rows.push(["Cuadratura por lote"]);
    rows.push(["Lote", "Numero", "Stock inicial", "Ingresos", "Salidas", "Stock final"]);
    data.lotes_detalle.forEach((l) => {
      rows.push([l.nombre, l.numero_lote, l.stock_inicial, l.ingresos, l.salidas, l.stock_final]);
    });
    const csv = rows.map((r) => r.map(cell).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reporte_${anio}_${String(mes).padStart(2, "0")}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportXLSX = () => {
    if (!data) return;
    const mesNombre = MESES[mes - 1];
    const wb = XLSX.utils.book_new();

    const resumen = [
      ["Reporte mensual", `${mesNombre} ${anio}`],
      [],
      ["Total ingresos (u)", data.total_ingresos],
      ["Total salidas (u)", data.total_salidas],
      ["Costo de ingresos", data.costo_total_ingresos],
      ["Valor inventario final", data.valor_inventario_final],
      ["Cuadratura", data.cuadra ? "OK" : "Inconsistente"],
    ];
    const wsR = XLSX.utils.aoa_to_sheet(resumen);
    wsR["!cols"] = [{ wch: 24 }, { wch: 18 }];
    XLSX.utils.book_append_sheet(wb, wsR, "Resumen");

    const prodRows = [["Producto", "Código", "Lotes", "Ingresos", "Salidas", "Stock final", "Costo ingresos", "Valor final"]];
    (data.productos_detalle || []).forEach((p) =>
      prodRows.push([p.nombre, p.codigo, p.num_lotes, p.ingresos, p.salidas, p.stock_final, p.costo_ingresos, p.valor_final]));
    const wsP = XLSX.utils.aoa_to_sheet(prodRows);
    wsP["!cols"] = [{ wch: 26 }, { wch: 12 }, { wch: 8 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 14 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, wsP, "Por producto");

    const loteRows = [["Lote", "Número", "Stock inicial", "Ingresos", "Salidas", "Stock final"]];
    (data.lotes_detalle || []).forEach((l) =>
      loteRows.push([l.nombre, l.numero_lote, l.stock_inicial, l.ingresos, l.salidas, l.stock_final]));
    const wsL = XLSX.utils.aoa_to_sheet(loteRows);
    wsL["!cols"] = [{ wch: 26 }, { wch: 16 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, wsL, "Por lote");

    XLSX.writeFile(wb, `reporte_${anio}_${String(mes).padStart(2, "0")}.xlsx`);
  };

  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 4; y--) years.push(y);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="reporte-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-[#0F172A]">Reportes</h1>
        <p className="text-[#475569] mt-1">Genera y exporta reportes de tu inventario en PDF, CSV o Excel.</p>
      </div>

      <Tabs defaultValue="mensual" className="space-y-6">
        <TabsList data-testid="reporte-tabs">
          <TabsTrigger value="mensual" data-testid="tab-mensual">Mensual</TabsTrigger>
          <TabsTrigger value="personalizado" data-testid="tab-personalizado">Personalizado</TabsTrigger>
        </TabsList>

        <TabsContent value="mensual" className="space-y-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <p className="text-[#475569]">Cuadratura de movimientos y valor del inventario por mes.</p>
            <div className="flex gap-2 items-center print:hidden flex-wrap">
          <Select value={String(mes)} onValueChange={(v) => setMes(parseInt(v, 10))}>
            <SelectTrigger className="w-40 bg-white border-[#E2E8F0]" data-testid="mes-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MESES.map((m, i) => (
                <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(anio)} onValueChange={(v) => setAnio(parseInt(v, 10))}>
            <SelectTrigger className="w-28 bg-white border-[#E2E8F0]" data-testid="anio-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            data-testid="export-pdf-button"
            variant="outline"
            onClick={exportPDF}
            disabled={loading || !data}
            className="border-[#E2E8F0]"
          >
            <Printer size={18} className="mr-1.5" /> Exportar PDF
          </Button>
          <Button
            data-testid="export-csv-button"
            variant="outline"
            onClick={exportCSV}
            disabled={loading || !data}
            className="border-[#E2E8F0]"
          >
            <DownloadSimple size={18} className="mr-1.5" /> Exportar CSV
          </Button>
          <Button
            data-testid="export-xlsx-button"
            variant="outline"
            onClick={exportXLSX}
            disabled={loading || !data}
            className="border-[#E2E8F0]"
          >
            <DownloadSimple size={18} className="mr-1.5" /> Exportar Excel
          </Button>
        </div>
      </div>

      {loading || !data ? (
        <div className="flex items-center justify-center py-32">
          <CircleNotch size={32} className="animate-spin text-[#0F172A]" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Metric label="Total ingresos" value={fmtNumber(data.total_ingresos)} sub="unidades" />
            <Metric label="Total salidas" value={fmtNumber(data.total_salidas)} sub="unidades" />
            <Metric label="Costo de ingresos" value={fmtMoney(data.costo_total_ingresos)} sub="compras del mes" />
            <Metric label="Valor inventario final" value={fmtMoney(data.valor_inventario_final)} sub="al cierre del mes" />
          </div>

          <div
            className={`rounded-md p-4 flex items-center gap-3 ${
              data.cuadra ? "bg-[#ECFDF5] border border-[#A7F3D0]" : "bg-[#FEF2F2] border border-[#FECACA]"
            }`}
            data-testid="cuadratura-banner"
          >
            {data.cuadra ? (
              <CheckCircle size={22} weight="bold" className="text-[#065F46]" />
            ) : (
              <XCircle size={22} weight="bold" className="text-[#991B1B]" />
            )}
            <p className={`text-sm font-semibold ${data.cuadra ? "text-[#065F46]" : "text-[#991B1B]"}`}>
              {data.cuadra
                ? "El reporte cuadra: stock inicial + ingresos − salidas = stock final."
                : "Atención: el reporte no cuadra. Revisa los movimientos."}
            </p>
          </div>

          {/* Costo total por producto */}
          <div className="bg-white border border-[#E2E8F0] rounded-md overflow-hidden">
            <div className="px-6 py-4 border-b border-[#E2E8F0]">
              <h2 className="font-heading text-lg font-bold text-[#0F172A]">Costo total por producto</h2>
            </div>
            {(data.productos_detalle || []).length === 0 ? (
              <p className="px-6 py-12 text-center text-[#64748B]">Sin movimientos en este periodo.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="productos-costo-table">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-[#64748B] bg-[#F8F9FA]">
                      <SortTh label="Producto" sortKey="nombre" activeKey={productosSort.sortKey} dir={productosSort.sortDir} onSort={productosSort.toggleSort} />
                      <SortTh label="Lotes" sortKey="num_lotes" activeKey={productosSort.sortKey} dir={productosSort.sortDir} onSort={productosSort.toggleSort} align="right" />
                      <SortTh label="Ingresos" sortKey="ingresos" activeKey={productosSort.sortKey} dir={productosSort.sortDir} onSort={productosSort.toggleSort} align="right" />
                      <SortTh label="Salidas" sortKey="salidas" activeKey={productosSort.sortKey} dir={productosSort.sortDir} onSort={productosSort.toggleSort} align="right" />
                      <SortTh label="Stock final" sortKey="stock_final" activeKey={productosSort.sortKey} dir={productosSort.sortDir} onSort={productosSort.toggleSort} align="right" />
                      <SortTh label="Costo ingresos" sortKey="costo_ingresos" activeKey={productosSort.sortKey} dir={productosSort.sortDir} onSort={productosSort.toggleSort} align="right" />
                      <SortTh label="Valor final" sortKey="valor_final" activeKey={productosSort.sortKey} dir={productosSort.sortDir} onSort={productosSort.toggleSort} align="right" />
                    </tr>
                  </thead>
                  <tbody>
                    {productosSort.sorted.map((p, i) => (
                      <tr key={i} className="border-t border-[#E2E8F0] hover:bg-[#F8F9FA] transition-colors">
                        <td className="px-6 py-3">
                          <div className="font-semibold text-[#0F172A]">{p.nombre}</div>
                          <div className="text-xs text-[#64748B]">{p.codigo}</div>
                        </td>
                        <td className="px-6 py-3 text-right text-[#475569]">{fmtNumber(p.num_lotes)}</td>
                        <td className="px-6 py-3 text-right text-[#065F46] font-medium">+{fmtNumber(p.ingresos)}</td>
                        <td className="px-6 py-3 text-right text-[#991B1B] font-medium">−{fmtNumber(p.salidas)}</td>
                        <td className="px-6 py-3 text-right text-[#0F172A]">{fmtNumber(p.stock_final)}</td>
                        <td className="px-6 py-3 text-right text-[#475569]">{fmtMoney(p.costo_ingresos)}</td>
                        <td className="px-6 py-3 text-right font-bold text-[#0F172A]">{fmtMoney(p.valor_final)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Cuadratura por lote */}
          <div className="bg-white border border-[#E2E8F0] rounded-md overflow-hidden">
            <div className="px-6 py-4 border-b border-[#E2E8F0]">
              <h2 className="font-heading text-lg font-bold text-[#0F172A]">Cuadratura por lote</h2>
            </div>
            {data.lotes_detalle.length === 0 ? (
              <p className="px-6 py-12 text-center text-[#64748B]">Sin movimientos en este periodo.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="cuadratura-table">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-[#64748B] bg-[#F8F9FA]">
                      <SortTh label="Lote" sortKey="nombre" activeKey={lotesSort.sortKey} dir={lotesSort.sortDir} onSort={lotesSort.toggleSort} />
                      <SortTh label="Stock inicial" sortKey="stock_inicial" activeKey={lotesSort.sortKey} dir={lotesSort.sortDir} onSort={lotesSort.toggleSort} align="right" />
                      <SortTh label="Ingresos" sortKey="ingresos" activeKey={lotesSort.sortKey} dir={lotesSort.sortDir} onSort={lotesSort.toggleSort} align="right" />
                      <SortTh label="Salidas" sortKey="salidas" activeKey={lotesSort.sortKey} dir={lotesSort.sortDir} onSort={lotesSort.toggleSort} align="right" />
                      <SortTh label="Stock final" sortKey="stock_final" activeKey={lotesSort.sortKey} dir={lotesSort.sortDir} onSort={lotesSort.toggleSort} align="right" />
                    </tr>
                  </thead>
                  <tbody>
                    {lotesSort.sorted.map((l, i) => (
                      <tr key={i} className="border-t border-[#E2E8F0] hover:bg-[#F8F9FA] transition-colors">
                        <td className="px-6 py-3">
                          <div className="font-semibold text-[#0F172A]">{l.nombre}</div>
                          <div className="text-xs text-[#64748B]">{l.numero_lote}</div>
                        </td>
                        <td className="px-6 py-3 text-right text-[#475569]">{fmtNumber(l.stock_inicial)}</td>
                        <td className="px-6 py-3 text-right text-[#065F46] font-medium">+{fmtNumber(l.ingresos)}</td>
                        <td className="px-6 py-3 text-right text-[#991B1B] font-medium">−{fmtNumber(l.salidas)}</td>
                        <td className="px-6 py-3 text-right font-bold text-[#0F172A]">{fmtNumber(l.stock_final)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Movimientos por día */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-[#E2E8F0] rounded-md overflow-hidden">
              <div className="px-6 py-4 border-b border-[#E2E8F0]">
                <h2 className="font-heading text-lg font-bold text-[#0F172A]">Movimientos por día</h2>
              </div>
              {data.por_dia.length === 0 ? (
                <p className="px-6 py-12 text-center text-[#64748B]">Sin actividad.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-[#64748B] bg-[#F8F9FA]">
                        <SortTh label="Día" sortKey="fecha" activeKey={porDiaSort.sortKey} dir={porDiaSort.sortDir} onSort={porDiaSort.toggleSort} />
                        <SortTh label="Ingresos" sortKey="ingresos" activeKey={porDiaSort.sortKey} dir={porDiaSort.sortDir} onSort={porDiaSort.toggleSort} align="right" />
                        <SortTh label="Salidas" sortKey="salidas" activeKey={porDiaSort.sortKey} dir={porDiaSort.sortDir} onSort={porDiaSort.toggleSort} align="right" />
                      </tr>
                    </thead>
                    <tbody>
                      {porDiaSort.sorted.map((d) => (
                        <tr key={d.fecha} className="border-t border-[#E2E8F0] hover:bg-[#F8F9FA] transition-colors">
                          <td className="px-6 py-3 text-[#0F172A]">{fmtDate(d.fecha)}</td>
                          <td className="px-6 py-3 text-right text-[#065F46] font-medium">+{fmtNumber(d.ingresos)}</td>
                          <td className="px-6 py-3 text-right text-[#991B1B] font-medium">−{fmtNumber(d.salidas)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="bg-white border border-[#E2E8F0] rounded-md overflow-hidden">
              <div className="px-6 py-4 border-b border-[#E2E8F0]">
                <h2 className="font-heading text-lg font-bold text-[#0F172A]">Detalle de movimientos</h2>
              </div>
              {data.movimientos.length === 0 ? (
                <p className="px-6 py-12 text-center text-[#64748B]">Sin movimientos.</p>
              ) : (
                <div className="overflow-y-auto max-h-[400px]">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0">
                      <tr className="text-left text-xs uppercase tracking-wider text-[#64748B] bg-[#F8F9FA]">
                        <SortTh label="Lote" sortKey="lote_nombre" activeKey={movimientosSort.sortKey} dir={movimientosSort.sortDir} onSort={movimientosSort.toggleSort} />
                        <SortTh label="Tipo" sortKey="tipo" activeKey={movimientosSort.sortKey} dir={movimientosSort.sortDir} onSort={movimientosSort.toggleSort} />
                        <SortTh label="Cant." sortKey="cantidad" activeKey={movimientosSort.sortKey} dir={movimientosSort.sortDir} onSort={movimientosSort.toggleSort} align="right" />
                      </tr>
                    </thead>
                    <tbody>
                      {movimientosSort.sorted.map((m) => (
                        <tr key={m.id} className="border-t border-[#E2E8F0] hover:bg-[#F8F9FA] transition-colors">
                          <td className="px-6 py-3 text-[#0F172A]">{m.lote_nombre}</td>
                          <td className="px-6 py-3">
                            <span className={`inline-flex items-center gap-1 text-xs font-bold ${m.tipo === "ingreso" ? "text-[#065F46]" : "text-[#991B1B]"}`}>
                              {m.tipo === "ingreso" ? <ArrowDown size={12} weight="bold" /> : <ArrowUp size={12} weight="bold" />}
                              {m.tipo === "ingreso" ? "Ingreso" : "Salida"}
                            </span>
                          </td>
                          <td className="px-6 py-3 text-right font-semibold text-[#0F172A]">{fmtNumber(m.cantidad)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
        </TabsContent>

        <TabsContent value="personalizado">
          <ReportePersonalizado />
        </TabsContent>
      </Tabs>
    </div>
  );
}
